import { useCallback, useEffect, useRef, useState } from "react";

import { getScrollableNodeFromRef } from "../../../Exercise/Search/utils/floatingUtils";
import {
  createNutritionTouchDragActivationGate,
  isNutritionTouchDragStartEvent,
} from "../../shared/nutritionTouchDragActivation";
import {
  buildNutritionPlanFoodMovePayloadFromTarget,
  resolveNutritionPlanFoodDragTarget,
} from "./nutritionPlanFoodDragTargeting";

function getResolvedSelectedOptionId(meal, selectedMealOptionIds) {
  const selectedOptionId = selectedMealOptionIds?.[meal?.id] ?? "";
  if (Array.isArray(meal?.options) && meal.options.some((option) => option?.id === selectedOptionId)) {
    return selectedOptionId;
  }

  return meal?.options?.[0]?.id ?? "";
}

function buildFrozenSelectedOptionIds(meals, selectedMealOptionIds) {
  return (Array.isArray(meals) ? meals : []).reduce((snapshot, meal) => {
    snapshot[meal.id] = getResolvedSelectedOptionId(meal, selectedMealOptionIds);
    return snapshot;
  }, {});
}

function getContainerKey(mealId, optionId) {
  return `${String(mealId ?? "").trim()}:${String(optionId ?? "").trim()}`;
}

function getFoodRowKey(mealId, optionId, itemId) {
  return `${getContainerKey(mealId, optionId)}:${String(itemId ?? "").trim()}`;
}

function areTargetsEqual(left, right) {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.mealId === right.mealId &&
    left.optionId === right.optionId &&
    left.insertionIndex === right.insertionIndex
  );
}

function getClientCoordinates(event) {
  const touch = event?.touches?.[0] ?? event?.changedTouches?.[0] ?? null;
  if (touch && Number.isFinite(touch.clientX) && Number.isFinite(touch.clientY)) {
    return {
      x: touch.clientX,
      y: touch.clientY,
    };
  }

  if (Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)) {
    return {
      x: event.clientX,
      y: event.clientY,
    };
  }

  return null;
}

function findFixedPositionContextNode(node) {
  let currentNode = node?.parentElement ?? null;
  while (currentNode) {
    const computedStyle = window.getComputedStyle?.(currentNode);
    if (!computedStyle) {
      currentNode = currentNode.parentElement;
      continue;
    }

    const createsContainingBlock =
      computedStyle.transform !== "none" ||
      computedStyle.perspective !== "none" ||
      computedStyle.filter !== "none" ||
      computedStyle.backdropFilter !== "none" ||
      computedStyle.contain === "paint" ||
      computedStyle.contain === "layout paint" ||
      computedStyle.willChange?.includes?.("transform") === true;

    if (createsContainingBlock) {
      return currentNode;
    }

    currentNode = currentNode.parentElement;
  }

  return null;
}

function getPositionContextRect(node) {
  if (!node?.getBoundingClientRect) {
    return {
      left: 0,
      top: 0,
    };
  }

  const rect = node.getBoundingClientRect();
  return {
    left: Number.isFinite(rect?.left) ? rect.left : 0,
    top: Number.isFinite(rect?.top) ? rect.top : 0,
  };
}

function buildFoodDragContainers({
  meals,
  selectedOptionIds,
  optionBodyNodes,
  foodRowNodes,
} = {}) {
  return (Array.isArray(meals) ? meals : [])
    .map((meal) => {
      const optionId = getResolvedSelectedOptionId(meal, selectedOptionIds);
      if (!optionId) {
        return null;
      }

      const option = (Array.isArray(meal?.options) ? meal.options : []).find(
        (candidateOption) => candidateOption?.id === optionId,
      );
      if (!option) {
        return null;
      }

      const optionBodyNode = optionBodyNodes.get(getContainerKey(meal.id, option.id));
      if (!optionBodyNode?.isConnected) {
        return null;
      }

      const rect = optionBodyNode.getBoundingClientRect?.();
      if (!rect || rect.width <= 0 || rect.height <= 0) {
        return null;
      }

      return {
        mealId: meal.id,
        optionId: option.id,
        rect,
        items: (Array.isArray(option.items) ? option.items : [])
          .map((item) => {
            const rowNode = foodRowNodes.get(getFoodRowKey(meal.id, option.id, item.id));
            if (!rowNode?.isConnected) {
              return null;
            }

            const rowRect = rowNode.getBoundingClientRect?.();
            if (!rowRect || rowRect.width <= 0 || rowRect.height <= 0) {
              return null;
            }

            return {
              itemId: item.id,
              rect: rowRect,
            };
          })
          .filter(Boolean),
      };
    })
    .filter(Boolean);
}

function findVerticalScrollableAncestor(node) {
  let currentNode = node?.parentElement ?? null;
  while (
    currentNode &&
    currentNode !== document.body &&
    currentNode !== document.documentElement
  ) {
    const computedStyle = window.getComputedStyle?.(currentNode);
    if (!computedStyle) {
      currentNode = currentNode.parentElement;
      continue;
    }

    const overflowY = computedStyle.overflowY;
    const overflow = computedStyle.overflow;
    const isScrollableOverflow =
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflow === "auto" ||
      overflow === "scroll";

    if (
      isScrollableOverflow &&
      currentNode.scrollHeight > currentNode.clientHeight
    ) {
      return currentNode;
    }

    currentNode = currentNode.parentElement;
  }

  return null;
}

function captureInlineStyle(node) {
  return {
    position: node.style.position,
    left: node.style.left,
    top: node.style.top,
    right: node.style.right,
    bottom: node.style.bottom,
    width: node.style.width,
    height: node.style.height,
    zIndex: node.style.zIndex,
    pointerEvents: node.style.pointerEvents,
    transform: node.style.transform,
    transition: node.style.transition,
    margin: node.style.margin,
    boxSizing: node.style.boxSizing,
    touchAction: node.style.touchAction,
    willChange: node.style.willChange,
  };
}

function restoreInlineStyle(node, snapshot) {
  if (!node || !snapshot) {
    return;
  }

  Object.entries(snapshot).forEach(([key, value]) => {
    node.style[key] = value ?? "";
  });
}

function buildSourceTarget(source) {
  return {
    mealId: source.mealId,
    optionId: source.optionId,
    insertionIndex: Math.max(0, Number(source.sourceIndex ?? 0) || 0),
  };
}

export function useNutritionPlanFoodsDragController({
  meals,
  selectedMealOptionIds,
  editorScrollRef,
  moveOptionItem,
} = {}) {
  const [activeFoodDrag, setActiveFoodDrag] = useState(null);
  const [frozenSelectedOptionIds, setFrozenSelectedOptionIds] = useState(null);
  const activeFoodDragSessionRef = useRef(null);
  const pointerCoordinatesRef = useRef(null);
  const optionBodyNodeMapRef = useRef(new Map());
  const foodRowNodeMapRef = useRef(new Map());
  const syncFrameRef = useRef(null);
  const autoScrollFrameRef = useRef(null);
  const latestMealsRef = useRef(meals);
  const latestSelectedMealOptionIdsRef = useRef(selectedMealOptionIds);
  const cleanupStylesRef = useRef(null);
  const pendingTouchDragActivationRef = useRef(null);

  useEffect(() => {
    latestMealsRef.current = meals;
  }, [meals]);

  useEffect(() => {
    latestSelectedMealOptionIdsRef.current = selectedMealOptionIds;
  }, [selectedMealOptionIds]);

  const cancelScheduledFrames = useCallback(() => {
    if (syncFrameRef.current != null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(syncFrameRef.current);
      syncFrameRef.current = null;
    }

    if (autoScrollFrameRef.current != null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  const cleanupLiftedRow = useCallback(() => {
    const cleanup = cleanupStylesRef.current;
    if (typeof cleanup === "function") {
      cleanup();
    }
    cleanupStylesRef.current = null;
  }, []);

  const clearPendingTouchDragActivation = useCallback(({ cancel = true } = {}) => {
    const pendingActivation = pendingTouchDragActivationRef.current;
    if (!pendingActivation) {
      return;
    }

    pendingTouchDragActivationRef.current = null;
    pendingActivation.detachListeners?.();
    if (cancel) {
      pendingActivation.gate?.cancel();
      return;
    }

    pendingActivation.gate?.dispose?.();
  }, []);

  const resetFoodDragSession = useCallback(() => {
    cancelScheduledFrames();
    pointerCoordinatesRef.current = null;
    activeFoodDragSessionRef.current = null;
    cleanupLiftedRow();
    clearPendingTouchDragActivation();
    setActiveFoodDrag(null);
    setFrozenSelectedOptionIds(null);
  }, [cancelScheduledFrames, cleanupLiftedRow, clearPendingTouchDragActivation]);

  useEffect(() => () => cancelScheduledFrames(), [cancelScheduledFrames]);

  useEffect(() => () => cleanupLiftedRow(), [cleanupLiftedRow]);

  useEffect(
    () => () => clearPendingTouchDragActivation(),
    [clearPendingTouchDragActivation],
  );

  const registerFoodOptionBodyNode = useCallback((mealId, optionId, node) => {
    const key = getContainerKey(mealId, optionId);
    if (node) {
      optionBodyNodeMapRef.current.set(key, node);
      return;
    }

    optionBodyNodeMapRef.current.delete(key);
  }, []);

  const registerFoodRowNode = useCallback((mealId, optionId, itemId, node) => {
    const key = getFoodRowKey(mealId, optionId, itemId);
    if (node) {
      foodRowNodeMapRef.current.set(key, node);
      return;
    }

    foodRowNodeMapRef.current.delete(key);
  }, []);

  const updateLiftedRowPosition = useCallback(() => {
    const session = activeFoodDragSessionRef.current;
    const pointerCoordinates = pointerCoordinatesRef.current;
    if (!session?.rowNode?.isConnected || !pointerCoordinates) {
      return;
    }

    const contextRect = getPositionContextRect(session.positionContextNode);
    session.rowNode.style.left = `${pointerCoordinates.x - session.anchorOffsetX - contextRect.left}px`;
    session.rowNode.style.top = `${pointerCoordinates.y - session.anchorOffsetY - contextRect.top}px`;
  }, []);

  const updateFoodDragProjection = useCallback(() => {
    const session = activeFoodDragSessionRef.current;
    const pointerCoordinates = pointerCoordinatesRef.current;
    if (!session || !pointerCoordinates) {
      return;
    }

    if (!session.rowNode?.isConnected) {
      resetFoodDragSession();
      return;
    }

    updateLiftedRowPosition();

    const containers = buildFoodDragContainers({
      meals: latestMealsRef.current,
      selectedOptionIds:
        session.frozenSelectedOptionIds ??
        latestSelectedMealOptionIdsRef.current,
      optionBodyNodes: optionBodyNodeMapRef.current,
      foodRowNodes: foodRowNodeMapRef.current,
    });
    const nextCurrentTarget = resolveNutritionPlanFoodDragTarget({
      activeItemId: session.itemId,
      pointerCoordinates,
      containers,
      previousTarget: session.currentTarget ?? session.displayTarget,
    });
    const nextDisplayTarget =
      nextCurrentTarget ?? session.displayTarget ?? session.sourceTarget;

    if (
      areTargetsEqual(session.currentTarget, nextCurrentTarget) &&
      areTargetsEqual(session.displayTarget, nextDisplayTarget)
    ) {
      return;
    }

    session.currentTarget = nextCurrentTarget;
    session.displayTarget = nextDisplayTarget;
    setActiveFoodDrag((currentSession) => {
      if (!currentSession) {
        return currentSession;
      }

      if (
        areTargetsEqual(currentSession.currentTarget, nextCurrentTarget) &&
        areTargetsEqual(currentSession.displayTarget, nextDisplayTarget)
      ) {
        return currentSession;
      }

      return {
        ...currentSession,
        currentTarget: nextCurrentTarget,
        displayTarget: nextDisplayTarget,
      };
    });
  }, [resetFoodDragSession, updateLiftedRowPosition]);

  const scheduleFoodDragSync = useCallback(() => {
    if (typeof requestAnimationFrame !== "function") {
      updateFoodDragProjection();
      return;
    }

    if (syncFrameRef.current != null) {
      return;
    }

    syncFrameRef.current = requestAnimationFrame(() => {
      syncFrameRef.current = null;
      updateFoodDragProjection();
    });
  }, [updateFoodDragProjection]);

  const finishFoodDrag = useCallback(
    ({ commitMove } = {}) => {
      const currentSession = activeFoodDragSessionRef.current;
      const movePayload =
        commitMove && currentSession
          ? buildNutritionPlanFoodMovePayloadFromTarget({
              meals: latestMealsRef.current,
              activeItem: currentSession,
              target: currentSession.currentTarget,
            })
          : null;

      resetFoodDragSession();

      if (movePayload) {
        moveOptionItem?.(movePayload);
      }
    },
    [moveOptionItem, resetFoodDragSession],
  );

  useEffect(() => {
    if (!activeFoodDrag) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      const session = activeFoodDragSessionRef.current;
      if (!session) {
        return;
      }

      if (
        session.inputMode === "pointer" &&
        Number.isFinite(session.pointerId) &&
        event?.pointerId !== session.pointerId
      ) {
        return;
      }

      const nextCoordinates = getClientCoordinates(event);
      if (!nextCoordinates) {
        return;
      }

      if (event?.cancelable && session.isTouchInput) {
        event.preventDefault();
      }

      pointerCoordinatesRef.current = nextCoordinates;
      scheduleFoodDragSync();
    };

    const handlePointerUp = (event) => {
      const session = activeFoodDragSessionRef.current;
      if (
        session?.inputMode === "pointer" &&
        Number.isFinite(session.pointerId) &&
        event?.pointerId !== session.pointerId
      ) {
        return;
      }

      finishFoodDrag({ commitMove: true });
    };

    const handlePointerCancel = (event) => {
      const session = activeFoodDragSessionRef.current;
      if (
        session?.inputMode === "pointer" &&
        Number.isFinite(session.pointerId) &&
        event?.pointerId !== session.pointerId
      ) {
        return;
      }

      finishFoodDrag({ commitMove: false });
    };

    if (activeFoodDrag.inputMode === "pointer") {
      window.addEventListener("pointermove", handlePointerMove, { passive: false });
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerCancel);

      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerCancel);
      };
    }

    window.addEventListener("mousemove", handlePointerMove, { passive: false });
    window.addEventListener("mouseup", handlePointerUp);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", handlePointerUp);
    window.addEventListener("touchcancel", handlePointerCancel);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
      window.removeEventListener("touchcancel", handlePointerCancel);
    };
  }, [activeFoodDrag, finishFoodDrag, scheduleFoodDragSync]);

  useEffect(() => {
    if (!activeFoodDrag) {
      return undefined;
    }

    scheduleFoodDragSync();
    const scrollNode = activeFoodDragSessionRef.current?.scrollNode ?? null;
    const handleScroll = () => {
      scheduleFoodDragSync();
    };
    const handleResize = () => {
      scheduleFoodDragSync();
    };

    scrollNode?.addEventListener?.("scroll", handleScroll, { passive: true });
    window.addEventListener?.("resize", handleResize);

    return () => {
      scrollNode?.removeEventListener?.("scroll", handleScroll);
      window.removeEventListener?.("resize", handleResize);
    };
  }, [activeFoodDrag, editorScrollRef, scheduleFoodDragSync]);

  useEffect(() => {
    if (!activeFoodDrag || typeof requestAnimationFrame !== "function") {
      return undefined;
    }

    const scrollNode = activeFoodDragSessionRef.current?.scrollNode ?? null;
    if (!scrollNode?.getBoundingClientRect) {
      return undefined;
    }

    let isActive = true;
    const tick = () => {
      if (!isActive) {
        return;
      }

      const pointerCoordinates = pointerCoordinatesRef.current;
      if (pointerCoordinates) {
        const scrollRect = scrollNode.getBoundingClientRect();
        const edgeThreshold = 88;
        let scrollDelta = 0;

        if (pointerCoordinates.y < scrollRect.top + edgeThreshold) {
          scrollDelta = -Math.min(
            18,
            Math.ceil((scrollRect.top + edgeThreshold - pointerCoordinates.y) / 6),
          );
        } else if (pointerCoordinates.y > scrollRect.bottom - edgeThreshold) {
          scrollDelta = Math.min(
            18,
            Math.ceil((pointerCoordinates.y - (scrollRect.bottom - edgeThreshold)) / 6),
          );
        }

        if (scrollDelta !== 0) {
          const maxScrollTop = Math.max(
            0,
            (Number(scrollNode.scrollHeight) || 0) - (Number(scrollNode.clientHeight) || 0),
          );
          const currentScrollTop = Number(scrollNode.scrollTop) || 0;
          const nextScrollTop = Math.max(
            0,
            Math.min(maxScrollTop, currentScrollTop + scrollDelta),
          );

          if (nextScrollTop !== currentScrollTop) {
            scrollNode.scrollTop = nextScrollTop;
            scheduleFoodDragSync();
          }
        }
      }

      autoScrollFrameRef.current = requestAnimationFrame(tick);
    };

    autoScrollFrameRef.current = requestAnimationFrame(tick);

    return () => {
      isActive = false;
      if (autoScrollFrameRef.current != null) {
        cancelAnimationFrame(autoScrollFrameRef.current);
        autoScrollFrameRef.current = null;
      }
    };
  }, [activeFoodDrag, editorScrollRef, scheduleFoodDragSync]);

  const beginFoodDrag = useCallback(
    ({
      mealId,
      optionId,
      itemId,
      sourceIndex,
      inputMode,
      pointerId = null,
      pointerCoordinates,
      activatorNode = null,
      isTouchInput = false,
    }) => {
      if (activeFoodDragSessionRef.current) {
        return;
      }
      const sourceNode = foodRowNodeMapRef.current.get(
        getFoodRowKey(mealId, optionId, itemId),
      );
      const sourceRect = sourceNode?.getBoundingClientRect?.();

      if (
        !pointerCoordinates ||
        !sourceNode ||
        !sourceRect ||
        sourceRect.width <= 0 ||
        sourceRect.height <= 0
      ) {
        return;
      }

      if (Number.isFinite(pointerId)) {
        activatorNode?.setPointerCapture?.(pointerId);
      }
      const frozenOptionIds = buildFrozenSelectedOptionIds(
        latestMealsRef.current,
        latestSelectedMealOptionIdsRef.current,
      );
      const sourceTarget = buildSourceTarget({
        mealId,
        optionId,
        sourceIndex,
      });
      const positionContextNode = findFixedPositionContextNode(sourceNode);
      const scrollNode =
        findVerticalScrollableAncestor(sourceNode) ??
        getScrollableNodeFromRef(editorScrollRef) ??
        null;
      const ownerDocument = sourceNode.ownerDocument ?? document;
      const bodyStyleSnapshot = {
        userSelect: ownerDocument.body?.style.userSelect ?? "",
        webkitUserSelect: ownerDocument.body?.style.webkitUserSelect ?? "",
      };
      const inlineStyleSnapshot = captureInlineStyle(sourceNode);

      cleanupStylesRef.current = () => {
        restoreInlineStyle(sourceNode, inlineStyleSnapshot);
        if (ownerDocument?.body?.style) {
          ownerDocument.body.style.userSelect = bodyStyleSnapshot.userSelect;
          ownerDocument.body.style.webkitUserSelect = bodyStyleSnapshot.webkitUserSelect;
        }
      };

      if (ownerDocument?.body?.style) {
        ownerDocument.body.style.userSelect = "none";
        ownerDocument.body.style.webkitUserSelect = "none";
      }

      sourceNode.style.position = "fixed";
      sourceNode.style.left = "0px";
      sourceNode.style.top = "0px";
      sourceNode.style.right = "auto";
      sourceNode.style.bottom = "auto";
      sourceNode.style.width = `${sourceRect.width}px`;
      sourceNode.style.height = `${sourceRect.height}px`;
      sourceNode.style.zIndex = "4000";
      sourceNode.style.pointerEvents = "none";
      sourceNode.style.transform = "none";
      sourceNode.style.transition = "none";
      sourceNode.style.margin = "0";
      sourceNode.style.boxSizing = "border-box";
      sourceNode.style.touchAction = "none";
      sourceNode.style.willChange = "left, top";

      pointerCoordinatesRef.current = pointerCoordinates;

      const session = {
        mealId,
        optionId,
        itemId,
        sourceIndex: Math.max(0, Number(sourceIndex ?? 0) || 0),
        pointerId: Number.isFinite(pointerId) ? pointerId : null,
        inputMode,
        isTouchInput,
        rowNode: sourceNode,
        positionContextNode,
        anchorOffsetX: pointerCoordinates.x - sourceRect.left,
        anchorOffsetY: pointerCoordinates.y - sourceRect.top,
        previewHeight: sourceRect.height,
        sourceTarget,
        currentTarget: sourceTarget,
        displayTarget: sourceTarget,
        frozenSelectedOptionIds: frozenOptionIds,
        scrollNode,
      };

      activeFoodDragSessionRef.current = session;
      setFrozenSelectedOptionIds(frozenOptionIds);
      setActiveFoodDrag({
        mealId,
        optionId,
        itemId,
        sourceIndex: session.sourceIndex,
        inputMode,
        isTouchInput,
        previewHeight: sourceRect.height,
        currentTarget: sourceTarget,
        displayTarget: sourceTarget,
      });
      updateLiftedRowPosition();
      scheduleFoodDragSync();
    },
    [editorScrollRef, scheduleFoodDragSync, updateLiftedRowPosition],
  );

  const startFoodDrag = useCallback(
    ({ event, mealId, optionId, itemId, sourceIndex }) => {
      if (
        !event ||
        activeFoodDragSessionRef.current ||
        pendingTouchDragActivationRef.current
      ) {
        return;
      }

      if (
        event.type !== "touchstart" &&
        typeof event.button === "number" &&
        event.button !== 0
      ) {
        return;
      }

      const pointerCoordinates = getClientCoordinates(event);
      if (!pointerCoordinates) {
        return;
      }

      const activatorNode = event.currentTarget ?? null;
      const isTouchInput = isNutritionTouchDragStartEvent(event);
      const inputMode = event.type === "touchstart"
        ? "touch"
        : event.type === "mousedown"
          ? "mouse"
          : "pointer";
      const pointerId = Number.isFinite(event.pointerId) ? event.pointerId : null;

      if (!isTouchInput) {
        event.preventDefault?.();
        event.stopPropagation?.();
        beginFoodDrag({
          mealId,
          optionId,
          itemId,
          sourceIndex,
          inputMode,
          pointerId,
          pointerCoordinates,
          activatorNode,
          isTouchInput,
        });
        return;
      }

      let detachListeners = () => {};
      const gate = createNutritionTouchDragActivationGate({
        initialCoordinates: pointerCoordinates,
        pointerId,
        onActivate: (activatedCoordinates) => {
          clearPendingTouchDragActivation({ cancel: false });
          beginFoodDrag({
            mealId,
            optionId,
            itemId,
            sourceIndex,
            inputMode,
            pointerId,
            pointerCoordinates: activatedCoordinates ?? pointerCoordinates,
            activatorNode,
            isTouchInput: true,
          });
        },
      });

      const handlePendingMove = (moveEvent) => {
        const nextCoordinates = getClientCoordinates(moveEvent);
        if (!nextCoordinates) {
          return;
        }

        if (gate.updateCoordinates(nextCoordinates, moveEvent?.pointerId)) {
          clearPendingTouchDragActivation();
        }
      };

      const handlePendingEnd = (endEvent) => {
        if (gate.cancel(endEvent?.pointerId)) {
          clearPendingTouchDragActivation({ cancel: false });
        }
      };

      if (event.type === "touchstart") {
        window.addEventListener("touchmove", handlePendingMove, { passive: true });
        window.addEventListener("touchend", handlePendingEnd);
        window.addEventListener("touchcancel", handlePendingEnd);
        detachListeners = () => {
          window.removeEventListener("touchmove", handlePendingMove);
          window.removeEventListener("touchend", handlePendingEnd);
          window.removeEventListener("touchcancel", handlePendingEnd);
        };
      } else {
        window.addEventListener("pointermove", handlePendingMove, { passive: true });
        window.addEventListener("pointerup", handlePendingEnd);
        window.addEventListener("pointercancel", handlePendingEnd);
        detachListeners = () => {
          window.removeEventListener("pointermove", handlePendingMove);
          window.removeEventListener("pointerup", handlePendingEnd);
          window.removeEventListener("pointercancel", handlePendingEnd);
        };
      }

      pendingTouchDragActivationRef.current = {
        gate,
        detachListeners,
      };
    },
    [beginFoodDrag, clearPendingTouchDragActivation],
  );

  const getFoodDragHandleProps = useCallback(
    ({ mealId, optionId, itemId, sourceIndex }) => {
      const start = (event) =>
        startFoodDrag({
          event,
          mealId,
          optionId,
          itemId,
          sourceIndex,
        });

      if (typeof window !== "undefined" && "PointerEvent" in window) {
        return {
          onPointerDown: start,
        };
      }

      return {
        onMouseDown: start,
        onTouchStart: start,
      };
    },
    [startFoodDrag],
  );

  return {
    isFoodDragActive: activeFoodDrag != null,
    activeFoodDragMealId: activeFoodDrag?.mealId ?? "",
    activeFoodDragItemId: activeFoodDrag?.itemId ?? "",
    foodDragTarget: activeFoodDrag?.displayTarget ?? null,
    foodDragPlaceholderHeight: activeFoodDrag?.previewHeight ?? 0,
    frozenSelectedOptionIds,
    registerFoodOptionBodyNode,
    registerFoodRowNode,
    getFoodDragHandleProps,
    cancelFoodDrag: () => finishFoodDrag({ commitMove: false }),
  };
}
